import { computeCoachingConfidence, defaultCoachAdvice } from './coachAdvice';
import { average, spread, clamp01, normalizeRange, titleCase } from './helpers';

function formatSeconds(ms) {
  if (!Number.isFinite(ms)) return '--';
  return `${(ms / 1000).toFixed(2)}s`;
}

function buildReferenceMap(report) {
  const fastestLapKeys = [...(report?.lapTotals || [])]
    .filter((item) => Number.isFinite(item?.totalMs))
    .sort((a, b) => a.totalMs - b.totalMs)
    .slice(0, 3)
    .map((item) => item.lapKey);

  return report.segments.reduce((acc, segment) => {
    const entries = report.entries.filter((entry) => (
      entry.segmentIndex === segment.index && fastestLapKeys.includes(entry.lapKey)
    ));

    acc.set(segment.index, {
      avgTimeMs: average(entries.map((entry) => entry.sectionTimeMs).filter(Number.isFinite)),
    });

    return acc;
  }, new Map());
}

function buildRows(report) {
  if (!report?.segments?.length || !report?.entries?.length) return [];
  const referenceMap = buildReferenceMap(report);

  return report.segments.map((segment) => {
    const entries = report.entries.filter((entry) => entry.segmentIndex === segment.index);
    if (!entries.length) return null;

    const times = entries.map((entry) => entry.sectionTimeMs).filter(Number.isFinite);
    const reference = referenceMap.get(segment.index) || null;
    const avgTimeMs = average(times);

    const row = {
      segmentIndex: segment.index,
      cornerId: segment.label,
      label: segment.label,
      sourceUsed: 'report',
      eventCount: entries.length,
      avgSectionTimeMs: avgTimeMs,
      spreadMs: spread(times),
      bestTimeMs: Math.min(...times),
      avgConsistencyPct: average(entries.map((entry) => entry.consistencyPct).filter(Number.isFinite)),
      reference,
      deltaMs: reference ? avgTimeMs - (reference.avgTimeMs ?? 0) : null,
    };

    const spreadPenalty = normalizeRange(row.spreadMs, 180, 650) * 0.55;
    const deltaPenalty = normalizeRange(row.deltaMs || 0, 120, 550) * 0.45;
    row.score = Math.max(0, Math.min(100, Math.round(100 - (spreadPenalty + deltaPenalty) * 100)));

    return row;
  }).filter(Boolean);
}

function classifySegmentTimeIssue(segment) {
  const eventCount = segment?.eventCount ?? 0;
  const deltaMs = segment?.deltaMs ?? null;
  const spreadMs = segment?.spreadMs ?? 0;

  if (eventCount < 2) {
    return {
      type: 'inconclusive',
      patternStrength: 0.1,
      outcomeCorrelation: 0,
      causalClarity: 0.1,
      signalNoise: 0.4,
    };
  }

  if (spreadMs >= 420) {
    return {
      type: 'inconsistent_timing',
      patternStrength: normalizeRange(spreadMs, 320, 800),
      outcomeCorrelation: 0.4,
      causalClarity: 0.78,
      signalNoise: 0.12,
    };
  }

  if (deltaMs != null && deltaMs >= 280) {
    return {
      type: 'consistently_slow',
      patternStrength: normalizeRange(deltaMs, 220, 700),
      outcomeCorrelation: normalizeRange(deltaMs, 220, 700) * 0.8,
      causalClarity: 0.74,
      signalNoise: 0.1,
    };
  }

  if (deltaMs != null && deltaMs >= 150 && spreadMs < 260) {
    return {
      type: 'repeatable_but_slow',
      patternStrength: normalizeRange(deltaMs, 120, 400),
      outcomeCorrelation: 0.55,
      causalClarity: 0.68,
      signalNoise: 0.08,
    };
  }

  if ((segment?.score ?? 0) >= 88) {
    return {
      type: 'stable',
      patternStrength: normalizeRange(segment.score, 88, 98),
      outcomeCorrelation: 0.4,
      causalClarity: 0.7,
      signalNoise: 0.06,
    };
  }

  return {
    type: 'mild_variation',
    patternStrength: 0.35,
    outcomeCorrelation: 0.22,
    causalClarity: 0.45,
    signalNoise: 0.14,
  };
}

function buildObservation(segment, diagnosis) {
  const eventNote = segment?.eventCount ? ` across ${segment.eventCount} laps` : '';

  switch (diagnosis.type) {
    case 'inconsistent_timing':
      return `${segment.label} varies by ${formatSeconds(segment.spreadMs)}${eventNote}. Average section time: ${formatSeconds(segment.avgSectionTimeMs)}. Best section time: ${formatSeconds(segment.bestTimeMs)}. The gap between your best and worst passes through this section is large enough that technique differences — not just traffic or conditions — are likely the cause.`;
    case 'consistently_slow':
      return `${segment.label} averages ${formatSeconds(segment.deltaMs)} slower than your best 3 laps${eventNote}. Average section time: ${formatSeconds(segment.avgSectionTimeMs)}. Best section time: ${formatSeconds(segment.bestTimeMs)}. The time spread is ${formatSeconds(segment.spreadMs)}, so this is a consistent pattern — you are leaving time on the table here every lap, not just on one bad run.`;
    case 'repeatable_but_slow':
      return `${segment.label} is repeatable (spread: ${formatSeconds(segment.spreadMs)}), but averages ${formatSeconds(segment.deltaMs)} off your best 3 laps${eventNote}. Average section time: ${formatSeconds(segment.avgSectionTimeMs)}. Best section time: ${formatSeconds(segment.bestTimeMs)}. The tight spread means your current approach is stable — you are consistently executing the same technique, but that technique has room for improvement.`;
    case 'stable':
      return `${segment.label} is one of your most repeatable sections${eventNote}: time spread of ${formatSeconds(segment.spreadMs)} and only ${segment.deltaMs != null ? formatSeconds(segment.deltaMs) : '--'} off your best-3 average. ${segment.score != null ? `Consistency score: ${segment.score}/100.` : ''} Both repeatability and pace are strong here.`;
    case 'inconclusive':
      return `Only ${segment.eventCount || 'a few'} clean passes recorded in ${segment.label}. There is not enough timing data to identify a reliable pattern yet.`;
    default:
      return `${segment.label} has some timing variation${eventNote} — spread: ${formatSeconds(segment.spreadMs)}, average: ${formatSeconds(segment.avgSectionTimeMs)}, best: ${formatSeconds(segment.bestTimeMs)} — but no single timing issue clearly stands out yet.`;
  }
}

function buildInference(diagnosis) {
  switch (diagnosis.type) {
    case 'inconsistent_timing':
      return 'A large time spread through a section usually means the line, braking, or throttle application is changing lap to lap. When section times vary this much, it is impossible to isolate a specific technique issue because each pass is effectively a different experiment. The priority is repeatability first — drive the section the same way 3-4 times in a row before trying to go faster. Once the spread tightens, the data will clearly show whether the remaining gap is in entry, mid-corner, or exit.';
    case 'consistently_slow':
      return 'This section is costing you time on every lap, not just occasionally. A consistent time deficit means your technique through this section has a ceiling that is below what the car and track allow. The good news is that your best section time proves the speed is achievable — you have done it before. The gap between your average and your best is the opportunity. Focus on what is different in your fastest pass: was the entry speed higher, the minimum speed higher, or the exit speed higher? That will tell you exactly where the time is hiding.';
    case 'repeatable_but_slow':
      return 'Repeatability is the foundation for speed, and you have that here — the spread is tight, which means your current technique is stable and consistent. That makes this an ideal section to test small, targeted changes because you can clearly measure whether a change helps or hurts. The tight spread also means you are probably not making mistakes — you are just executing a slightly conservative version of the optimal technique. One small change in braking distance, throttle pickup point, or minimum speed could unlock the gap without adding inconsistency.';
    case 'stable':
      return 'Both time spread and pace are strong in this section, meaning your technique is not only consistent but also close to your potential. This is the standard to aim for across the rest of the lap. Pay attention to what makes this section feel right — your visual references, the rhythm of your inputs, the confidence level. That internal calibration is exactly what the less consistent sections need.';
    default:
      return 'The timing data shows some variation, but the pattern does not clearly point to one cause. This can happen when multiple small differences overlap across entry, mid-corner, and exit. A few more clean, focused laps will help separate the signals and reveal the dominant factor.';
  }
}

function buildTitle(diagnosis, segment) {
  switch (diagnosis.type) {
    case 'inconsistent_timing':
      return `${segment.label} needs more repeatable timing`;
    case 'consistently_slow':
      return `${segment.label} is costing persistent time`;
    case 'repeatable_but_slow':
      return `${segment.label} is tidy but still slow`;
    case 'stable':
      return `${segment.label} is a segment-time benchmark`;
    case 'inconclusive':
      return `${segment.label} needs more segment-time data`;
    default:
      return `${segment.label} has mild segment-time variation`;
  }
}

function buildType(diagnosis) {
  if (diagnosis.type === 'stable') return 'positive';
  if (diagnosis.type === 'inconclusive') return 'neutral';
  return 'warning';
}

export function buildSegmentTimeCoachCards({
  report,
}) {
  const segments = buildRows(report);
  if (!segments.length) return [];

  const cards = segments.map((segment) => {
    const diagnosis = classifySegmentTimeIssue(segment);
    const confidence = computeCoachingConfidence({
      sourceUsed: 'report',
      eventCount: segment.eventCount,
      patternStrength: diagnosis.patternStrength,
      outcomeCorrelation: diagnosis.outcomeCorrelation,
      causalClarity: diagnosis.causalClarity,
      signalNoise: diagnosis.signalNoise,
    });

    return {
      id: `segment-time-${segment.segmentIndex}-${diagnosis.type}`,
      cornerId: segment.label,
      title: buildTitle(diagnosis, segment),
      type: buildType(diagnosis),
      diagnosis: diagnosis.type,
      observation: confidence.score >= 35 ? buildObservation(segment, diagnosis) : null,
      inference: confidence.score >= 50 ? buildInference(diagnosis) : null,
      recommendation: confidence.score >= 35 ? defaultCoachAdvice.getAdvice({
        domain: 'segmentTime',
        diagnosisType: diagnosis.type,
        confidenceLabel: confidence.label,
        segment,
      }) : null,
      confidence,
      priority: Math.round((diagnosis.patternStrength * 100) + (confidence.score * 0.55)),
      meta: `${titleCase(confidence.label)} confidence`,
      referenceLabel: 'Best 3 laps',
    };
  }).filter((card) => card.confidence.score >= 35);

  const allActionable = cards
    .filter((card) => card.diagnosis !== 'stable' && card.diagnosis !== 'inconclusive')
    .sort((a, b) => b.priority - a.priority);
  const actionable = allActionable.slice(0, 2);

  const benchmark = cards
    .filter((card) => card.diagnosis === 'stable')
    .sort((a, b) => b.confidence.score - a.confidence.score)[0];

  let positiveCard = benchmark;
  if (!positiveCard) {
    const actionableIds = new Set(actionable.map((c) => c.id));
    const candidate = cards
      .filter((card) => !actionableIds.has(card.id) && card.diagnosis !== 'inconclusive')
      .sort((a, b) => b.confidence.score - a.confidence.score)[0];
    if (candidate) {
      positiveCard = {
        ...candidate,
        type: 'positive',
        title: `${candidate.cornerId ? candidate.cornerId : 'Best section'} is your most repeatable section`,
        diagnosis: 'best_available',
        recommendation: defaultCoachAdvice.getAdvice({
          domain: 'segmentTime',
          diagnosisType: 'best_available',
          confidenceLabel: candidate.confidence.label,
          segment: { label: candidate.cornerId },
        }),
      };
    }
  }

  return [
    ...actionable,
    ...(positiveCard ? [positiveCard] : []),
  ];
}
